// @ts-nocheck

/**
 * This is the main component that is rendered for all LmC form fields. It
 * has a fixed prop structure (e.g. `label`, `required`) but is flexible enough
 * for use in (hopefully) any form scenario.
 */

import { debounce, get, isArray, isEqual, without } from 'lodash'
import PropTypes from 'prop-types'
import React, { Suspense } from 'react'
import { connect } from 'react-redux'
import Actions from '../../actions'
import { isDependentFieldHidden } from '../../common/validation/functions'
import Operations from '../../operations'
import AddressField from './types/AddressField'
import AntdSelectField from './types/AntdSelectField'
import BirthdateField from './types/BirthdateField'
import BubbleTextInput from './types/BubbleTextInput'
import CheckboxField from './types/CheckboxField'
import DateField from './types/DateField'
import DateTimeField from './types/DateTimeField'
import DualTextField from './types/DualTextField'
import DurationField from './types/DurationField'
import FileUploadField from './types/FileUploadField'
import FormattedTextField from './types/FormattedTextField'
import LogCategoryItemField from './types/LogCategoryItemField'
import MeasurementField from './types/MeasurementField'
import MultiSelectField from './types/MultiSelectField'
import NoEditTextField from './types/NoEditTextField'
import NumberField from './types/NumberField'
import PasswordEditField from './types/PasswordEditField'
import RadiosField from './types/RadiosField'
import SelectField from './types/SelectField'
import SingleCheckboxField from './types/SingleCheckboxField'
import SwitchField from './types/SwitchField'
// Field type imports (converted from React.lazy to fix "Loading..." issue)
// NOTE: Some field types MUST use React.lazy because they import Field themselves,
// creating a circular dependency if imported directly.
import TextAreaField from './types/TextAreaField'
import TextField from './types/TextField'
import TimeField from './types/TimeField'
import ZoneField from './types/ZoneField'

// These field types import Field internally, so they MUST use React.lazy to avoid circular dependency
const UserGeneratedFieldLayout = React.lazy(
  () => import('./types/UserGeneratedFieldLayout')
)
const RiskCalculationField = React.lazy(
  () => import('./types/RiskCalculationField')
)
const BmiCalculationField = React.lazy(
  () => import('./types/BmiCalculationField')
)
const MedicationFrequencyField = React.lazy(
  () => import('./types/MedicationFrequencyField')
)
const MedicationRoundsField = React.lazy(
  () => import('./types/MedicationRoundsField')
)

const WAIT_TIME = 500

const FIELDS = {
  address: AddressField,
  antdSelect: AntdSelectField,
  birthdate: BirthdateField,
  bmiCalculation: BmiCalculationField,
  bubble: BubbleTextInput,
  changePassword: PasswordEditField,
  checkbox: CheckboxField,
  date: DateField,
  datetime: DateTimeField,
  duration: DurationField,
  file: FileUploadField,
  formattedText: FormattedTextField,
  logCategoryItems: LogCategoryItemField,
  medicationFrequency: MedicationFrequencyField,
  medicationRounds: MedicationRoundsField,
  multiSelect: MultiSelectField,
  multiText: DualTextField,
  radios: RadiosField,
  userGeneratedFieldLayout: UserGeneratedFieldLayout,
  riskCalculation: RiskCalculationField,
  noEditText: NoEditTextField,
  select: SelectField,
  singleCheckbox: SingleCheckboxField,
  switch: SwitchField,
  text: TextField,
  time: TimeField,
  number: NumberField,
  measurement: MeasurementField,
  zone: ZoneField,
  textarea: TextAreaField
}

export class Field extends React.Component {
  componentDidMount() {
    this.setValidation(this.props.keys)
  }

  componentDidUpdate(prevProps) {
    const { clearValidation, keys, readOnly, required } = this.props

    if (
      !readOnly &&
      (!isEqual(prevProps.keys, keys) || prevProps.required !== required)
    ) {
      clearValidation(prevProps.keys)
      this.setValidation(keys)
    }
  }

  componentWillUnmount() {
    const { clearValidation, readOnly, keys } = this.props
    if (!readOnly) clearValidation(keys)
  }

  // eslint-disable-next-line class-methods-use-this
  isMultiField = (keys) => isArray(keys) && keys.length > 1

  // eslint-disable-next-line class-methods-use-this
  getMultiFieldLabel = (key) => {
    const labelKey = Object.keys(MULTI_FIELD_LABELS).find((lKey) =>
      key.match(new RegExp(`${lKey}$`))
    )
    return labelKey ? MULTI_FIELD_LABELS[labelKey] : undefined
  }

  handleChange = ({ key, value, file, uri }) => {
    const { addFormFile, onChange, setFormField } = this.props
    if (file && uri) {
      addFormFile({ key, file, uri })
    } else {
      setFormField({ key, value })
      if (onChange) {
        onChange(value)
      }
    }
    this.validate(key)
  }

  setValidation = (keys) => {
    const {
      dependsOn,
      readOnly,
      label,
      errorHelpText,
      required,
      setValidation,
      validationTypes
    } = this.props as unknown as any

    if (readOnly || (!dependsOn && !required && !validationTypes)) {
      return
    }

    if (this.isMultiField(keys)) {
      keys.forEach((key) => {
        let validationLabel = this.getMultiFieldLabel(key) || label
        if (errorHelpText) {
          validationLabel += ` (${errorHelpText})`
        }
        setValidation({
          dependsOn,
          key,
          label: validationLabel,
          required,
          relatedKeys: keys.filter((k) => k !== key),
          validationTypes
        })
      })
    } else {
      let validationLabel = label
      if (errorHelpText) validationLabel += ` (${errorHelpText})`
      setValidation({
        dependsOn,
        key: keys,
        label: validationLabel,
        required,
        validationTypes
      })
    }
  }

  validate(key) {
    debounce(() => {
      const { keys, validate } = this.props as unknown as any
      validate(key)
      if (this.isMultiField(keys)) {
        Object.keys(without(keys, key)).forEach((relatedKey) => {
          validate(relatedKey)
        })
      }
    }, WAIT_TIME)
  }

  render() {
    const {
      dependsOn,
      readOnly,
      formId,
      keys,
      placeholders,
      onChange,
      type,
      values,
      ...props
    } = this.props

    /**
     * Don't render the fields if the form isn't in the redux state (i.e.
     * there is no `formId`). Read-only forms/fields may not require redux
     * data.
     */
    if (!readOnly && !formId) return null

    /**
     * Don't render hidden dependent fields. For the `userGeneratedFieldLayout`
     * field type, we still render the hidden field, as we have opening / closing
     * animations for that type.
     */
    if (
      type !== 'userGeneratedFieldLayout' &&
      dependsOn &&
      isDependentFieldHidden(values, dependsOn)
    ) {
      return null
    }

    // Get the correct field to render based on the `type` prop
    const FieldType = FIELDS[type]
    if (!FieldType) {
      console.error(`Specified field type (${type}) does not exist.`)
      return null
    }

    /**
     * If the field has multiple keys, short-circuit the normal field rendering
     * as the multi-fields have slightly different prop requirements.
     */
    if (this.isMultiField(keys)) {
      return (
        <Suspense fallback={<div>Loading...</div>}>
          <FieldType
            formId={formId}
            keys={keys}
            onChange={this.handleChange}
            placeholders={placeholders}
            readOnly={readOnly}
            type={type}
            values={values}
            {...props}
          />
        </Suspense>
      )
    }

    const formKey = isArray(keys) ? keys[0] : keys
    const placeholder = isArray(placeholders) ? placeholders[0] : placeholders

    return (
      <Suspense fallback={<div>Loading...</div>}>
        <FieldType
          formId={formId}
          formKey={formKey}
          onChange={this.handleChange}
          placeholder={placeholder}
          readOnly={readOnly}
          values={values}
          {...props}
        />
      </Suspense>
    )
  }
}

const MULTI_FIELD_LABELS = {
  'name.first': 'First name',
  'name.last': 'Last name',
  startTime: 'Start time',
  endTime: 'End time',
  password: 'Password',
  password_confirm: 'Password confirmation'
}

Field.propTypes = {
  addFormFile: PropTypes.func.isRequired,
  dependsOn: PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.any
  }),
  errors: PropTypes.object,
  formId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  keys: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  label: PropTypes.string,
  errorHelpText: PropTypes.string,
  note: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  options: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.object)
  ]),
  placeholders: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  required: PropTypes.bool,
  layout: PropTypes.oneOf(['row', 'column', 'no-label']),
  style: PropTypes.object,
  type: PropTypes.oneOf([
    'address',
    'antdSelect',
    'birthdate',
    'bmiCalculation',
    'bubble',
    'changePassword',
    'checkbox',
    'date',
    'dateTime',
    'duration',
    'file',
    'formattedText',
    'measurement',
    'medicationFrequency',
    'medicationRounds',
    'multiSelect',
    'multiText',
    'number',
    'radios',
    'riskAssessment',
    'riskCalculation',
    'noEditText',
    'select',
    'singleCheckbox',
    'switch',
    'text',
    'textarea',
    'userGeneratedFieldLayout',
    'zone'
  ]),
  validationTypes: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  values: PropTypes.object.isRequired,
  hideShortSeizure: PropTypes.bool
}

Field.defaultProps = {
  readOnly: false
}

const mapStateToProps = (state, props) => ({
  errors: get(state, `forms[${props.formId}].validationErrors`)
})

const mapDispatchToProps = (dispatch, props) => ({
  addFormFile: ({ key, file, uri }) =>
    dispatch(
      Actions.addFormFile({
        key,
        file,
        uri,
        formId: props.formId
      })
    ),
  clearValidation: (keys) =>
    dispatch(
      Actions.clearFieldValidation({
        keys,
        formId: props.formId
      })
    ),
  validate: (key) =>
    dispatch(
      Operations.validateField({
        key,
        formId: props.formId
      })
    ),
  setValidation: (data) =>
    dispatch(
      Actions.setFieldValidation({
        ...data,
        formId: props.formId
      })
    ),
  setFormField: ({ key, value }) =>
    dispatch(
      Actions.setFormField({
        key,
        value,
        formId: props.formId
      })
    )
})

export default connect(mapStateToProps, mapDispatchToProps)(Field)
